import { BaseLogger } from "@app/config/loggers/baseLogger";
import { CLIENT_EVENT, EventLogger } from "@app/config/loggers/eventLogger";
import { hasCsrfToken } from "@base/lib/auth";
import { takeLoginAttempt } from "@base/lib/loginAttempt";
import { currentUserQueryOptions, useCurrentUserQuery } from "@base/model/hooks";
import { clearDailyLogSearchDataOfOtherUsers } from "@features/project-detail/model/dailyLogSearchNavigation";
import { BricksShell } from "@layouts/bricks-shell";
import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { Outlet, createFileRoute, redirect } from "@tanstack/react-router";
import axios from "axios";
import { useEffect } from "react";

function AuthenticatedLayout() {
  const { data } = useCurrentUserQuery();
  const queryClient = useQueryClient();
  const user = data?.user;
  const roles = data?.roles;
  const userId = user?.id;

  useEffect(
    function clearPreviousUserSearchData() {
      if (userId === undefined) return;
      clearDailyLogSearchDataOfOtherUsers(queryClient, userId);
    },
    [queryClient, userId],
  );

  useEffect(() => {
    if (!user) return;

    Sentry.setUser({
      id: String(user.id),
      email: user.email,
      username: user.name,
    });

    BaseLogger.identify(String(user.id), {
      name: user.name,
      email: user.email,
      roles,
      source: user.source,
      isEmailVerified: user.is_email_verified,
      serviceDueDate: user.service_due_date,
    });

    // 인증을 마치고 처음 돌아온 순간에만 시도 표시가 남아 있다. 이미 로그인된 채로
    // 앱을 다시 연 경우에는 표시가 없고, 그건 로그인이 아니므로 보고하지 않는다.
    // identify 뒤에 보내야 이 이벤트가 익명이 아닌 해당 사용자에게 귀속된다.
    // 표시는 읽는 즉시 소비되므로 쿼리 refetch 로 이 effect 가 다시 돌아도 중복되지 않는다.
    const attempt = takeLoginAttempt();
    if (attempt) {
      EventLogger.result(CLIENT_EVENT.LOGIN_SUCCESS, { provider: "google", duration_ms: attempt.durationMs });
    }

    return () => {
      Sentry.setUser(null);
    };
  }, [user, roles]);

  return (
    <BricksShell>
      <Outlet />
    </BricksShell>
  );
}

export const Route = createFileRoute("/_authenticated")({
  beforeLoad: async ({ context }) => {
    // navigator.onLine === false(비행기 모드 등)일 때 networkMode:"online" 설정으로 인해
    // ensureQueryData가 Promise를 영구 hang시키므로 선행 처리한다
    const isDefinitelyOffline = !navigator.onLine;
    if (isDefinitelyOffline) {
      if (hasCsrfToken()) return;
      throw redirect({ to: "/login" });
    }

    try {
      await context.queryClient.ensureQueryData(currentUserQueryOptions);
    } catch (error) {
      const isNetworkError = axios.isAxiosError(error) && !error.response;
      if (isNetworkError && hasCsrfToken()) return;
      throw redirect({ to: "/login" });
    }
  },
  component: AuthenticatedLayout,
});
